import React, { useContext, useEffect, useState } from 'react';
import { IStyleContext, StyleContext } from './styleState/styleContext';
import { Form } from 'antd';
import { FormPage } from 'components/EntityForm/FormPage';
import { FormSection } from 'components/EntityForm/FormSection';
import { FormSideTabs } from 'components/EntityForm/FormSideTabs';
import { FormSideTab } from 'components/EntityForm/FormSideTab';
import { FormActions } from 'components/EntityForm/FormActions';
import { NoDataNew } from 'components/NoResultsFound/NoDataNew';
import { StyleFieldsNew } from 'components/StyleFields/StyleFieldsNew';
import { LanguageBar } from './components/LanguageBar';
import { MediaFields } from './components/MediaFields';
import { DocumentSettings } from './components/DocumentSettings';
import { ModalWindows } from './components/ModalWindows';
import classNames from 'classnames';

const colorsGroup = { id: 'colorsID', key: 'colorsKey' };

interface IProps {
	isVisible: boolean;
}

export const StyleForm: React.FC<IProps> = ({ isVisible }) => {
	const {
		setStyleSettings,
		setGroup,
		changeTypeOfModalWindow,
		mediaSettingsForUpdate,
		documentSettingsForUpdate,
		selectedGroup,
		styleSettingColors,
		groupsForLoader,
		loading,
	} = useContext<IStyleContext>(StyleContext);
	const [isFirstEntry, setIsFirstEntry] = useState<boolean>(true);
	const [form] = Form.useForm();
	const activeGroupId = selectedGroup?.id ?? colorsGroup.id;

	useEffect(() => {
		if (isFirstEntry && isVisible && loading) {
			setIsFirstEntry(false);
		}
	}, [isVisible, loading]);

	const handleSubmit = () => {
		changeTypeOfModalWindow('toConfirmSubmit');
	};

	const handleCancel = () => {
		changeTypeOfModalWindow('toConfirmFieldsClearing');
	};

	return (
		<div className={classNames({ hidden: !isVisible })} data-test="StyleForm">
			{mediaSettingsForUpdate.length ? (
				<FormPage form={form} testId="StyleFormPage" onFinish={handleSubmit}>
					<div className="flex items-start gap-4">
						<FormSideTabs>
							<FormSideTab
								translationKey="colors"
								translationNameSpace="mediaSettings"
								isActive={activeGroupId === colorsGroup.id}
								testId="SidebarItem"
								onClick={() => setGroup(colorsGroup)}
							/>
							{mediaSettingsForUpdate[0].groups.map((el) => (
								<FormSideTab
									key={el.id}
									translationKey={el.key}
									translationNameSpace="mediaSettings"
									isActive={activeGroupId === el.id}
									testId="SidebarItem"
									onClick={() => setGroup({ id: el.id, key: el.key })}
								/>
							))}
							{documentSettingsForUpdate.map((el) => (
								<FormSideTab
									key={el.id}
									translationKey={el.key}
									translationNameSpace="mediaSettings"
									isActive={activeGroupId === el.id}
									testId="SidebarItem"
									onClick={() => setGroup({ id: el.id, key: el.key || '' })}
								/>
							))}
						</FormSideTabs>
						<div className="min-w-0 flex-1 flex flex-col gap-4">
							<LanguageBar />
							<div className={classNames({ hidden: activeGroupId !== colorsGroup.id })}>
								<FormSection
									translationKey="colors"
									translationNameSpace="mediaSettings"
									testId="ColorsSection"
									isCollapsible={false}>
									<StyleFieldsNew
										styleSettingColors={styleSettingColors}
										setStyleSettings={setStyleSettings}
									/>
								</FormSection>
							</div>
							{mediaSettingsForUpdate.map((el) => (
								<MediaFields key={el.languageId} data={el} activeGroupId={activeGroupId} />
							))}
							{documentSettingsForUpdate.map((el) => (
								<div key={el.id} className={classNames({ hidden: activeGroupId !== el.id })}>
									<FormSection
										translationKey={el.key || ''}
										translationNameSpace="mediaSettings"
										testId={`DocumentSettings-${el.key}`}
										isCollapsible={false}>
										<DocumentSettings data={el} />
									</FormSection>
								</div>
							))}
							<FormActions isSaveDisabled={!!groupsForLoader.length} onCancel={handleCancel} />
						</div>
					</div>
				</FormPage>
			) : (
				!loading &&
				!isFirstEntry && (
					<div className="p-4">
						<NoDataNew />
					</div>
				)
			)}
			<ModalWindows />
		</div>
	);
};
